import { forwardRef, TextareaHTMLAttributes, useState } from "react";
import classNames from "classnames";
import styles from "./ui.module.scss";

interface IFloatingTextAreaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value"> {
  label: string;
  value: string;
  maxLength: number;
  errorMessage?: string;
}

/** The design system's multi-line field, with the "0 / 2000" counter under it. */
const FloatingTextArea = forwardRef<
  HTMLTextAreaElement,
  IFloatingTextAreaProps
>(
  (
    { label, value, maxLength, errorMessage, onFocus, onBlur, ...props },
    ref
  ) => {
    const [focused, setFocused] = useState(false);
    const floating = focused || value !== "";

    return (
      <div className={styles.field}>
        <label
          className={classNames(styles.box, styles.areaBox, {
            [styles.boxFocused]: focused,
            [styles.boxError]: !!errorMessage,
          })}>
          <span className={styles.boxContent}>
            <span
              className={classNames(styles.label, {
                [styles.areaLabelResting]: !floating,
              })}>
              {label}
            </span>
            <textarea
              ref={ref}
              {...props}
              value={value}
              maxLength={maxLength}
              className={styles.textarea}
              onFocus={(event) => {
                setFocused(true);
                onFocus?.(event);
              }}
              onBlur={(event) => {
                setFocused(false);
                onBlur?.(event);
              }}
            />
          </span>
        </label>
        <div className={styles.counter}>
          <span
            className={classNames(styles.counterValue, {
              [styles.counterValueEmpty]: value.length === 0,
            })}>
            {value.length}
          </span>
          <span>/</span>
          <span>{maxLength}</span>
        </div>
        {errorMessage && <p className={styles.error}>{errorMessage}</p>}
      </div>
    );
  }
);

FloatingTextArea.displayName = "FloatingTextArea";

export default FloatingTextArea;
